import { useEffect, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useVault } from "@/context/VaultContext";
import { CameraCapture } from "@/components/aph/CameraCapture";

const BGS = ["#12151C", "#06343D", "#3A1518", "#1B1630"];

export function StoryComposer({ open, onOpenChange }) {
  const { postStory } = useVault();
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [text, setText] = useState("");
  const [bg, setBg] = useState(BGS[0]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!blob) return setUrl(null);
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  const close = (o) => { if (!o) { setBlob(null); setText(""); } onOpenChange(o); };

  const post = async (payload) => {
    setBusy(true);
    try {
      await postStory(payload);
      toast.success("Story posted for 24 hours");
      close(false);
    } catch (e) {
      toast.error(e.message || "Could not post story");
    } finally {
      setBusy(false);
    }
  };

  const btn = "w-full flex items-center justify-center gap-2 bg-stellar text-void font-branding font-semibold rounded-secure py-2.5 disabled:opacity-50";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="story-composer" className="bg-nebula border-rim text-ash sm:max-w-sm rounded-secure">
        <DialogHeader>
          <DialogTitle className="font-branding">New story</DialogTitle>
          <DialogDescription className="text-ash/50">Sealed separately for each contact. Vanishes after 24 hours.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="photo">
          <TabsList className="grid grid-cols-2 bg-void rounded-secure">
            <TabsTrigger value="photo" data-testid="story-tab-photo" className="rounded-secure data-[state=active]:bg-nebula data-[state=active]:text-stellar">Photo</TabsTrigger>
            <TabsTrigger value="text" data-testid="story-tab-text" className="rounded-secure data-[state=active]:bg-nebula data-[state=active]:text-stellar">Text</TabsTrigger>
          </TabsList>
          <TabsContent value="photo" className="pt-3 space-y-3">
            {open && !blob && <CameraCapture onCapture={setBlob} testPrefix="story" />}
            {url && (
              <>
                <img src={url} alt="Story preview" className="w-full aspect-[3/4] object-cover rounded-secure border border-glass" data-testid="story-preview" />
                <div className="flex gap-2">
                  <button onClick={() => setBlob(null)} data-testid="story-retake-btn" className="flex-1 flex items-center justify-center gap-2 border border-rim rounded-secure py-2.5 text-sm"><RotateCcw size={16} strokeWidth={1.5} /> Retake</button>
                  <button onClick={() => post({ blob })} disabled={busy} data-testid="story-post-photo-btn" className={`flex-1 ${btn}`}>{busy && <Loader2 size={16} className="animate-spin" />} Post</button>
                </div>
              </>
            )}
          </TabsContent>
          <TabsContent value="text" className="pt-3 space-y-3">
            <div className="aspect-[3/4] rounded-secure border border-glass flex items-center justify-center p-6" style={{ background: bg }}>
              <textarea data-testid="story-text-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Say something…"
                className="w-full bg-transparent text-center font-branding text-xl font-semibold text-ash placeholder-ash/30 focus:outline-none resize-none" rows={5} />
            </div>
            <div className="flex gap-2 justify-center">
              {BGS.map((c, i) => (
                <button key={c} onClick={() => setBg(c)} data-testid={`story-bg-${i}`} className={`w-8 h-8 rounded-full border-2 ${bg === c ? "border-stellar" : "border-rim"}`} style={{ background: c }} />
              ))}
            </div>
            <button onClick={() => post({ text: text.trim(), bg })} disabled={busy || !text.trim()} data-testid="story-post-text-btn" className={btn}>{busy && <Loader2 size={16} className="animate-spin" />} Post</button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
